import React, { useEffect, useState } from 'react';

import { getRoot } from 'mobx-easy';
import { Task } from '@bringg/types';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { getTasksWeight } from '@bringg-frontend/shared-tasks-weight';
import { loadRouteGroups } from '@bringg-frontend/monitoring';

import { useStores } from 'bringg-web/recipes';
import UserAssignerModal from 'bringg-web/features/runs-assignment/user-assignment/user-assigner-modal';
import useRouteMonitoringStore from '../../routes-monitoring-screen/stores/helpers/use-route-monitoring-store';
import RootStore from 'bringg-web/stores/root-store';
import useVehicleType from 'bringg-web/hooks/use-vehicle-type';
import { isClusterModeEnabled } from 'bringg-web/components/routes-planner-modal/routes-planner-data-creator/routes-planner-data-creator';
import { CloseDataType } from 'bringg-web/features/runs-assignment/user-assignment/consts';

type BasicProps = {
	closeModal: (data?: CloseDataType) => void;
	doNotNotifyServerOnAssign?: boolean;
};

interface PropsWithRoute extends BasicProps {
	selectedTasks?: Task[];
	routeId: number;
}

interface PropsWithTasks extends BasicProps {
	routeId?: number;
	selectedTasks: Task[];
}

type Props = PropsWithRoute | PropsWithTasks;

const UserAssignerTool = ({ routeId, selectedTasks, closeModal, doNotNotifyServerOnAssign }: Props) => {
	const { driversStore, merchantConfigurationsStore, runStore } = useStores();
	const [isLoading, setIsLoading] = useState(true);
	const [tasksWeight, setTasksWeight] = useState({ maxWeight: 0, weightCapacity: 0 });
	const [isAllRunWasSelected, setIsAllRunWasSelected] = useState(false);
	const [detailedRoute, setDetailedRoute] = useState(null);

	const {
		routeMonitoringActionsStore: { startAssignDriverListeners },
		routesMonitoringStore
	} = useRouteMonitoringStore();

	const { getVehicleTypeByVehicleIds } = useVehicleType();

	const fetchData = async (teamId: number) => {
		const [isFullRoute] = await Promise.all([
			isFullRouteWasSelected(),
			driversStore.fetchByTeam(teamId),
			getWeight()
		]);

		if (isFullRoute) {
			const selectedRouteId = routeId ? routeId : selectedTasks[0].run_id;
			const detailedRoute = runStore.get(selectedRouteId);
			setDetailedRoute(detailedRoute);
			setIsAllRunWasSelected(isFullRoute);
		}
		setIsLoading(false);
	};

	useEffect(() => {
		const detailedRoute = runStore.get(routeId);
		const teamId = routeId ? detailedRoute.team_id : selectedTasks[0].team_ids[0];
		setDetailedRoute(detailedRoute);
		fetchData(teamId);
	}, []);

	const getDrivers = () => {
		const teamId = routeId ? detailedRoute.team_id : selectedTasks[0].team_ids[0];
		return driversStore.driversByTeam.get(teamId) || [];
	};

	const onCloseModal = data => {
		closeModal(data);
		if (data && selectedTasks) {
			routesMonitoringStore.refreshData();
		}
	};

	const getUserTypeWeightCapacity = (): number => {
		const firstTask: Task = selectedTasks?.[0];
		if (firstTask.user_id) {
			const user = getRoot<RootStore>().data.driversStore.get(firstTask.user_id);
			return getRoot<RootStore>().data.userTypeStore.get(user.user_type_id)?.max_total_weight;
		}
	};

	const getWeight = async () => {
		const tasksIds = selectedTasks?.map(task => task.id) || [];
		const fullTasks = await getRoot<RootStore>().data.tasksStore.loadMany(tasksIds);
		const isClusterMode = await isClusterModeEnabled();
		const weightData = getTasksWeight(
			fullTasks,
			merchantConfigurationsStore.ignoreInventoryQuantityForCapacityCalculation,
			merchantConfigurationsStore.uniqueInventoryFields,
			merchantConfigurationsStore.enableVehicles,
			getVehicleTypeByVehicleIds(),
			getUserTypeWeightCapacity,
			isClusterMode,
			detailedRoute
		);

		setTasksWeight(weightData);
	};

	const isFullRouteWasSelected = async () => {
		if (routeId) {
			return true;
		}
		const selectedTasksByRun: { [key: string]: number[] } = {};
		selectedTasks.forEach(task => {
			if (selectedTasksByRun[task.run_id]) {
				selectedTasksByRun[task.run_id].push(task.id);
			} else {
				selectedTasksByRun[task.run_id] = [task.id];
			}
		});
		const selectedRouteIds = Object.keys(selectedTasksByRun).map(routeId => +routeId);
		if (selectedRouteIds.length > 1) {
			return false;
		}
		const taskTeamId = selectedTasks.find(task => task.team_ids[0]);

		const runsWithNoFullTasksLoad = Object.entries(selectedTasksByRun).find(([runId, selectedTasks]) => {
			const run = runStore.get(+runId);
			return run.task_ids.length > selectedTasks.length;
		});
		if (runsWithNoFullTasksLoad) {
			const taskByRoute = await loadRouteGroups(selectedRouteIds, taskTeamId.team_ids[0]);

			const partialRouteSelected = Object.entries(selectedTasksByRun).find(
				([key, value]) => taskByRoute.items_by_group[key].length !== value.length
			);

			return !partialRouteSelected;
		}
		return true;
	};

	if (isLoading) {
		return <BringgIcon iconName={BringgFontIcons.Loader} spin />;
	}

	const getRouteTasks = () => {
		if (!detailedRoute) {
			return undefined;
		}
		return detailedRoute.task_ids.map(taskId => getRoot<RootStore>().data.tasksStore.get(taskId));
	};

	return (
		<UserAssignerModal
			onClose={onCloseModal}
			modalParams={{
				source: {
					run: detailedRoute,
					tasks: selectedTasks,
					selectedTasksWeight: tasksWeight,
					isAllRunWasSelected,
					runTasks: getRouteTasks()
				},
				readyToExecute: detailedRoute ? detailedRoute.ready_to_execute : selectedTasks[0].ready_to_execute,
				data: {
					drivers: getDrivers()
				}
			}}
			startRealtimeAsync={() => !doNotNotifyServerOnAssign && startAssignDriverListeners(routeId, selectedTasks)}
		/>
	);
};

export default UserAssignerTool;
